import React, { useState } from 'react';
import { Play, Calendar, CalendarCheck, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { PrimaryButton } from '@/components/ui';
import { TRIED_RATINGS } from '@/data';
import type { Clip, ScheduledActivity, CoupleData, ActivityFollowUp, ActivityReflection, TriedClip, HisLoadFeedback } from '@/types';
import type { TriedRatingValue } from '@/data/ratings';
import { now, generateId } from '@/lib/utils';

interface ActivityFollowUpModalProps {
  activity: ScheduledActivity;
  clip: Clip | undefined;
  coupleData: CoupleData;
  remainingCount: number;
  onComplete: (followUp: ActivityFollowUp, triedClip?: TriedClip, newActivity?: ScheduledActivity) => void;
  onSkip: () => void;
  onDismissAll: () => void;
  onWatchClip?: (clip: Clip) => void;
}

type Step = 'did-it' | 'his-reflection' | 'her-reflection' | 'his-load' | 'not-completed';

// Load feedback scales
const VOLUME_SCALE = [
  { value: 1, label: 'Light', emoji: '💧' },
  { value: 2, label: 'Normal', emoji: '💦' },
  { value: 3, label: 'Good', emoji: '💦💦' },
  { value: 4, label: 'Heavy', emoji: '🌊' },
  { value: 5, label: 'Massive', emoji: '🌊🌊' },
];

const THICKNESS_SCALE = [
  { value: 1, label: 'Watery', emoji: '💧' },
  { value: 2, label: 'Thin', emoji: '🥛' },
  { value: 3, label: 'Normal', emoji: '🍦' },
  { value: 4, label: 'Thick', emoji: '🍨' },
  { value: 5, label: 'Very Thick', emoji: '🧁' },
];

const TASTE_SCALE = [
  { value: 1, label: 'Bad', emoji: '😖' },
  { value: 2, label: 'Not great', emoji: '😕' },
  { value: 3, label: 'Neutral', emoji: '😐' },
  { value: 4, label: 'Good', emoji: '😊' },
  { value: 5, label: 'Yummy', emoji: '😋' },
];

const DISTANCE_SCALE = [
  { value: 1, label: 'Dribble', emoji: '💧' },
  { value: 2, label: 'Short', emoji: '📏' },
  { value: 3, label: 'Normal', emoji: '🎯' },
  { value: 4, label: 'Good distance', emoji: '🚀' },
  { value: 5, label: 'Shooter!', emoji: '💥' },
];

const INTENSITY_SCALE = [
  { value: 1, label: 'Mild', emoji: '😌' },
  { value: 2, label: 'Nice', emoji: '😊' },
  { value: 3, label: 'Good', emoji: '😍' },
  { value: 4, label: 'Intense', emoji: '🤤' },
  { value: 5, label: 'Mind-blowing', emoji: '🤯' },
];

const NOT_COMPLETED_REASONS = [
  { value: 'scheduling', label: 'Scheduling conflict' },
  { value: 'not-mood', label: "Weren't in the mood" },
  { value: 'something-else', label: 'Did something else instead' },
  { value: 'forgot', label: 'Forgot about it' },
  { value: 'other', label: 'Other' }
];

export const ActivityFollowUpModal: React.FC<ActivityFollowUpModalProps> = ({
  activity,
  clip,
  coupleData,
  remainingCount,
  onComplete,
  onSkip,
  onDismissAll,
  onWatchClip
}) => {
  const [step, setStep] = useState<Step>('did-it');
  const [didComplete, setDidComplete] = useState<boolean | null>(null);

  // His reflection state
  const [hisReflection, setHisReflection] = useState<Partial<ActivityReflection>>({
    howItWent: '',
    differenceFromClip: '',
    nextTimeDifferently: '',
    rating: undefined
  });

  // Her reflection state
  const [herReflection, setHerReflection] = useState<Partial<ActivityReflection>>({
    howItWent: '',
    differenceFromClip: '',
    nextTimeDifferently: '',
    rating: undefined
  });

  // His load feedback state
  const [hisLoadFeedback, setHisLoadFeedback] = useState<Partial<HisLoadFeedback>>({});

  // Not completed state
  const [notCompletedReason, setNotCompletedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [wantsToReschedule, setWantsToReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>();

  const handleDidIt = (completed: boolean) => {
    setDidComplete(completed);
    if (completed) {
      setStep('his-reflection');
    } else {
      setStep('not-completed');
    }
  };

  const handleSubmit = () => {
    const followUp: ActivityFollowUp = {
      id: generateId('afu'),
      scheduledActivityId: activity.id,
      clipId: activity.clipId,
      coupleId: coupleData.coupleId,
      scheduledDate: activity.scheduledDate,
      clipTitle: clip?.title,
      didComplete: didComplete!,
      hisReflection: didComplete ? hisReflection as ActivityReflection : undefined,
      herReflection: didComplete ? herReflection as ActivityReflection : undefined,
      hisLoadFeedback: didComplete && Object.keys(hisLoadFeedback).length > 0 ? hisLoadFeedback as HisLoadFeedback : undefined,
      notCompletedReason: !didComplete ? (notCompletedReason === 'other' ? customReason : notCompletedReason) : undefined,
      promptedAt: now(),
      respondedAt: now(),
      status: 'completed'
    };

    // Create TriedClip for dual-write if completed
    let triedClip: TriedClip | undefined;
    if (didComplete && (hisReflection.rating || herReflection.rating)) {
      triedClip = {
        clipId: activity.clipId,
        rating: (hisReflection.rating || herReflection.rating) as TriedRatingValue,
        hisThoughts: hisReflection.howItWent || '',
        herThoughts: herReflection.howItWent || '',
        triedAt: now()
      };
    }

    // Create new scheduled activity if rescheduling
    let newActivity: ScheduledActivity | undefined;
    if (!didComplete && wantsToReschedule && rescheduleDate) {
      const rescheduledDate = rescheduleDate.toISOString().split('T')[0] ?? '';
      const scheduledActivity: ScheduledActivity = {
        id: generateId('sched'),
        clipId: activity.clipId,
        scheduledDate: rescheduledDate,
        createdAt: now(),
        notes: activity.notes
      };
      newActivity = scheduledActivity;
      followUp.rescheduledToActivityId = scheduledActivity.id;
    }

    onComplete(followUp, triedClip, newActivity);
  };

  const canSubmit = () => {
    if (step === 'his-load') {
      // His load step is optional, can always submit
      return true;
    }
    if (step === 'not-completed') {
      // Need a reason
      if (!notCompletedReason) return false;
      if (notCompletedReason === 'other' && !customReason.trim()) return false;
      if (wantsToReschedule && !rescheduleDate) return false;
      return true;
    }
    return false;
  };

  const canContinueToLoad = () => {
    // Need at least one rating to continue
    return hisReflection.rating || herReflection.rating;
  };

  const getRatingEmoji = (rating: TriedRatingValue) => {
    switch (rating) {
      case 'hot': return '🔥🔥🔥';
      case 'good': return '🔥🔥';
      case 'meh': return '🔥';
      case 'nope': return '❌';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-lg border border-zinc-700 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="w-12 h-12 rounded-full bg-lime-500/20 flex items-center justify-center mx-auto mb-3">
            <CalendarCheck className="w-6 h-6 text-lime-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Activity Check-in</h2>
          <p className="text-zinc-400 text-sm">
            You had something scheduled for {new Date(activity.scheduledDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </p>
          {remainingCount > 1 && (
            <p className="text-zinc-500 text-xs mt-1">
              {remainingCount} activities to review
            </p>
          )}
        </div>

        {/* Clip preview */}
        {clip && (
          <button
            onClick={() => onWatchClip?.(clip)}
            className="w-full bg-zinc-700/50 hover:bg-zinc-700 rounded-lg p-3 transition-colors group mb-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-zinc-600 flex items-center justify-center group-hover:bg-lime-500/30 transition-colors">
                <Play className="w-6 h-6 text-zinc-400 group-hover:text-lime-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-white font-medium text-sm truncate">{clip.title}</p>
                <p className="text-zinc-500 text-xs">Tap to watch</p>
              </div>
            </div>
          </button>
        )}

        {/* Step: Did you do it? */}
        {step === 'did-it' && (
          <div className="space-y-4">
            <p className="text-white text-center font-medium">Did you do it?</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDidIt(true)}
                className="flex-1 px-4 py-4 rounded-xl text-lg font-medium border-2 border-lime-500 bg-lime-500/20 text-lime-400 hover:bg-lime-500/30 transition-colors"
              >
                ✅ Yes, we did!
              </button>
              <button
                onClick={() => handleDidIt(false)}
                className="flex-1 px-4 py-4 rounded-xl text-lg font-medium border-2 border-zinc-600 bg-zinc-700 text-zinc-300 hover:bg-zinc-600 transition-colors"
              >
                Not this time
              </button>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={onSkip}
                className="text-zinc-400 text-sm hover:text-zinc-200"
              >
                Skip
              </button>
              {remainingCount > 1 && (
                <button
                  onClick={onDismissAll}
                  className="text-zinc-500 text-sm hover:text-zinc-300"
                >
                  Dismiss all
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step: His Reflection */}
        {step === 'his-reflection' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-blue-400 font-medium">His perspective</span>
              <span className="text-zinc-500 text-xs">(1 of 2)</span>
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-1">How did it go?</label>
              <textarea
                value={hisReflection.howItWent || ''}
                onChange={e => setHisReflection({ ...hisReflection, howItWent: e.target.value })}
                placeholder="Describe the experience..."
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-1">How different was it from the clip?</label>
              <textarea
                value={hisReflection.differenceFromClip || ''}
                onChange={e => setHisReflection({ ...hisReflection, differenceFromClip: e.target.value })}
                placeholder="What was similar or different?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-1">What would you do differently next time?</label>
              <textarea
                value={hisReflection.nextTimeDifferently || ''}
                onChange={e => setHisReflection({ ...hisReflection, nextTimeDifferently: e.target.value })}
                placeholder="Any learnings for next time?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-2">Rating</label>
              <div className="flex gap-2">
                {TRIED_RATINGS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setHisReflection({ ...hisReflection, rating: r.value })}
                    className={`flex-1 px-3 py-2 rounded-lg text-center border ${
                      hisReflection.rating === r.value
                        ? 'border-lime-500 bg-lime-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{getRatingEmoji(r.value)}</span>
                    <p className="text-xs text-zinc-400 mt-1">{r.label}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep('did-it')}
                className="px-4 py-2 bg-zinc-700 text-zinc-300 rounded-xl hover:bg-zinc-600"
              >
                Back
              </button>
              <PrimaryButton
                onClick={() => setStep('her-reflection')}
                className="flex-1"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </PrimaryButton>
            </div>
          </div>
        )}

        {/* Step: Her Reflection */}
        {step === 'her-reflection' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-pink-400 font-medium">Her perspective</span>
              <span className="text-zinc-500 text-xs">(2 of 2)</span>
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-1">How did it go?</label>
              <textarea
                value={herReflection.howItWent || ''}
                onChange={e => setHerReflection({ ...herReflection, howItWent: e.target.value })}
                placeholder="Describe the experience..."
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-1">How different was it from the clip?</label>
              <textarea
                value={herReflection.differenceFromClip || ''}
                onChange={e => setHerReflection({ ...herReflection, differenceFromClip: e.target.value })}
                placeholder="What was similar or different?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-1">What would you do differently next time?</label>
              <textarea
                value={herReflection.nextTimeDifferently || ''}
                onChange={e => setHerReflection({ ...herReflection, nextTimeDifferently: e.target.value })}
                placeholder="Any learnings for next time?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-300 mb-2">Rating</label>
              <div className="flex gap-2">
                {TRIED_RATINGS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setHerReflection({ ...herReflection, rating: r.value })}
                    className={`flex-1 px-3 py-2 rounded-lg text-center border ${
                      herReflection.rating === r.value
                        ? 'border-pink-500 bg-pink-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{getRatingEmoji(r.value)}</span>
                    <p className="text-xs text-zinc-400 mt-1">{r.label}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep('his-reflection')}
                className="px-4 py-2 bg-zinc-700 text-zinc-300 rounded-xl hover:bg-zinc-600"
              >
                Back
              </button>
              <PrimaryButton
                onClick={() => setStep('his-load')}
                disabled={!canContinueToLoad()}
                className="flex-1"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </PrimaryButton>
            </div>
          </div>
        )}

        {/* Step: His Load Feedback */}
        {step === 'his-load' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-blue-400 font-medium">His Load</span>
              <span className="text-zinc-500 text-xs">(optional but helpful!)</span>
            </div>
            <p className="text-zinc-400 text-sm">How was his finish? This helps track progress over time.</p>

            {/* Volume */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Volume</label>
              <div className="flex gap-1">
                {VOLUME_SCALE.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setHisLoadFeedback({ ...hisLoadFeedback, volume: s.value })}
                    className={`flex-1 px-2 py-2 rounded-lg text-center border ${
                      hisLoadFeedback.volume === s.value
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{s.emoji}</span>
                    <p className="text-[10px] text-zinc-400 mt-1">{s.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Thickness */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Thickness</label>
              <div className="flex gap-1">
                {THICKNESS_SCALE.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setHisLoadFeedback({ ...hisLoadFeedback, thickness: s.value })}
                    className={`flex-1 px-2 py-2 rounded-lg text-center border ${
                      hisLoadFeedback.thickness === s.value
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{s.emoji}</span>
                    <p className="text-[10px] text-zinc-400 mt-1">{s.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Taste (if applicable) */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Taste <span className="text-zinc-500">(if she tasted)</span></label>
              <div className="flex gap-1">
                {TASTE_SCALE.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setHisLoadFeedback({ ...hisLoadFeedback, taste: s.value })}
                    className={`flex-1 px-2 py-2 rounded-lg text-center border ${
                      hisLoadFeedback.taste === s.value
                        ? 'border-pink-500 bg-pink-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{s.emoji}</span>
                    <p className="text-[10px] text-zinc-400 mt-1">{s.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Distance */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Distance/Force</label>
              <div className="flex gap-1">
                {DISTANCE_SCALE.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setHisLoadFeedback({ ...hisLoadFeedback, distance: s.value })}
                    className={`flex-1 px-2 py-2 rounded-lg text-center border ${
                      hisLoadFeedback.distance === s.value
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{s.emoji}</span>
                    <p className="text-[10px] text-zinc-400 mt-1">{s.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Orgasm Intensity */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">His Orgasm Intensity</label>
              <div className="flex gap-1">
                {INTENSITY_SCALE.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setHisLoadFeedback({ ...hisLoadFeedback, intensity: s.value })}
                    className={`flex-1 px-2 py-2 rounded-lg text-center border ${
                      hisLoadFeedback.intensity === s.value
                        ? 'border-lime-500 bg-lime-500/20'
                        : 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600'
                    }`}
                  >
                    <span className="text-lg">{s.emoji}</span>
                    <p className="text-[10px] text-zinc-400 mt-1">{s.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm text-zinc-300 mb-1">Any notes?</label>
              <textarea
                value={hisLoadFeedback.notes || ''}
                onChange={e => setHisLoadFeedback({ ...hisLoadFeedback, notes: e.target.value })}
                placeholder="Anything noteworthy about his load? Diet changes, supplements, buildup time, etc."
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep('her-reflection')}
                className="px-4 py-2 bg-zinc-700 text-zinc-300 rounded-xl hover:bg-zinc-600"
              >
                Back
              </button>
              <PrimaryButton
                onClick={handleSubmit}
                disabled={!canSubmit()}
                className="flex-1"
              >
                Save Feedback
              </PrimaryButton>
            </div>
          </div>
        )}

        {/* Step: Not Completed */}
        {step === 'not-completed' && (
          <div className="space-y-4">
            <p className="text-zinc-300 text-sm">No worries! What happened?</p>

            <div className="space-y-2">
              {NOT_COMPLETED_REASONS.map(reason => (
                <button
                  key={reason.value}
                  onClick={() => setNotCompletedReason(reason.value)}
                  className={`w-full px-4 py-3 rounded-lg text-left text-sm border ${
                    notCompletedReason === reason.value
                      ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                      : 'border-zinc-600 bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                  }`}
                >
                  {reason.label}
                </button>
              ))}
            </div>

            {notCompletedReason === 'other' && (
              <textarea
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                placeholder="What happened?"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
              />
            )}

            {/* Reschedule option */}
            <div className="pt-2">
              <button
                onClick={() => setWantsToReschedule(!wantsToReschedule)}
                className={`w-full px-4 py-3 rounded-lg text-left text-sm border flex items-center gap-2 ${
                  wantsToReschedule
                    ? 'border-lime-500 bg-lime-500/20 text-lime-400'
                    : 'border-zinc-600 bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Would you like to reschedule?
              </button>
            </div>

            {wantsToReschedule && (
              <div className="bg-zinc-700/50 rounded-lg p-3">
                <DayPicker
                  mode="single"
                  selected={rescheduleDate}
                  onSelect={setRescheduleDate}
                  disabled={{ before: new Date() }}
                  className="!bg-transparent [&_.rdp-day]:text-white [&_.rdp-day_button]:hover:bg-zinc-600 [&_.rdp-day_button.rdp-day_selected]:bg-lime-500 [&_.rdp-caption]:text-white [&_.rdp-head_cell]:text-zinc-400"
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep('did-it')}
                className="px-4 py-2 bg-zinc-700 text-zinc-300 rounded-xl hover:bg-zinc-600"
              >
                Back
              </button>
              <PrimaryButton
                onClick={handleSubmit}
                disabled={!canSubmit()}
                className="flex-1"
              >
                {wantsToReschedule && rescheduleDate ? 'Save & Reschedule' : 'Save'}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
